/**
 * Flavor Cards Addon for Dawn Theme
 * Version: 1.0.0
 * Standalone JavaScript - No conflicts with Dawn theme
 */

(function() {
  'use strict';

  class FlavorCardsAddon {
    constructor(container) {
      this.container = container;
      this.cards = container.querySelectorAll('.flavor-card-addon');
      this.quantityInputs = container.querySelectorAll('.flavor-card-addon__quantity-input');
      this.quantityMeter = container.querySelector('[data-quantity-meter-addon]');

      this.selectedVariants = new Map();
      this.productForm = document.querySelector('form[action*="/cart/add"]');

      // 🎯 Prix de base unitaire (sans remise) - pour le prix barré
      this.baseUnitPrice = 2990; // Valeur par défaut

      // Configuration par défaut des paliers
      this.tiers = [
        { qty: 1, text: '1 bottle', price: '$29.99/ea' },
        { qty: 2, text: '2 bottles', price: '$28.99/ea' },
        { qty: 3, text: '3 bottles', price: '$27.99/ea' },
        { qty: 4, text: '4+ bottles', price: '$26.99/ea' }
      ];

      this.init();
    }

    init() {
      this.loadConfiguration();
      this.bindEvents();
      this.initializeQuantityButtons();
      this.updateQuantityMeter();
      this.updateProductForm();
    }

    loadConfiguration() {
      // Charger la configuration depuis les data attributes du container
      const config = this.container.dataset;

      // 🎯 Charger le prix de base unitaire
      if (config.baseUnitPrice) {
        this.baseUnitPrice = parseInt(config.baseUnitPrice) || 2990;
      }

      if (config.tiers) {
        try {
          this.tiers = JSON.parse(config.tiers);
        } catch (e) {
          console.warn('Invalid tiers configuration, using defaults');
        }
      }
    }

    bindEvents() {
      // Quantity input events
      this.quantityInputs.forEach(input => {
        const quantityContainer = input.closest('.flavor-card-addon__quantity-controls');
        const plusButton = quantityContainer.querySelector('[name="plus"]');
        const minusButton = quantityContainer.querySelector('[name="minus"]');
        
        // Button clicks
        plusButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.incrementQuantity(input);
        });

        minusButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.decrementQuantity(input);
        });
      });

      // Card click events
      this.cards.forEach(card => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('.flavor-card-addon__quantity-controls')) return;
          this.handleCardClick(card);
        });
      });
    }

    handleQuantityChangeDirectly(input, quantity) {
      const variantId = input.dataset.variantId;
      const card = input.closest('.flavor-card-addon');

      // Validate quantity
      const max = parseInt(input.dataset.max) || 999;

      if (quantity < 0) {
        input.value = 0;
        quantity = 0;
      }

      if (quantity > max) {
        input.value = max;
        quantity = max;
      }

      // Update selected variants
      if (quantity > 0) {
        this.selectedVariants.set(variantId, {
          quantity: quantity,
          price: parseInt(card.dataset.variantPrice),
          basePrice: this.baseUnitPrice, // 🎯 Prix de base configuré manuellement (pour prix barré)
          title: card.querySelector('.flavor-card-addon__title').textContent.trim()
        });
        card.classList.add('flavor-card-addon--selected');
      } else {
        this.selectedVariants.delete(variantId);
        card.classList.remove('flavor-card-addon--selected');
      }

      this.updateQuantityButtons(input);
      this.updateQuantityMeter();
      this.updateProductForm();
      this.updateSummary();
    }

    incrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      if (currentValue < max) {
        const newValue = currentValue + 1;
        input.value = newValue;
        this.handleQuantityChangeDirectly(input, newValue);
      }
    }

    decrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;

      if (currentValue > 0) {
        const newValue = currentValue - 1;
        input.value = newValue;
        this.handleQuantityChangeDirectly(input, newValue);
      }
    }

    updateQuantityButtons(input) {
      const quantityContainer = input.closest('.flavor-card-addon__quantity-controls');
      const plusButton = quantityContainer.querySelector('[name="plus"]');
      const minusButton = quantityContainer.querySelector('[name="minus"]');

      const value = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      const shouldDisableMinus = value <= 0;
      const shouldDisablePlus = value >= max;

      minusButton.disabled = shouldDisableMinus;
      plusButton.disabled = shouldDisablePlus;

      minusButton.classList.toggle('disabled', shouldDisableMinus);
      plusButton.classList.toggle('disabled', shouldDisablePlus);
    }

    handleCardClick(card) {
      const input = card.querySelector('.flavor-card-addon__quantity-input');
      if (!input || card.classList.contains('flavor-card-addon--unavailable')) return;
      
      const currentQuantity = parseInt(input.value) || 0;
      if (currentQuantity === 0) {
        this.incrementQuantity(input);
      }
    }

    initializeQuantityButtons() {
      this.quantityInputs.forEach(input => {
        this.updateQuantityButtons(input);
      });
    }

    calculateDiscountedPrice() {
      let totalQuantity = 0;
      this.selectedVariants.forEach(data => {
        totalQuantity += data.quantity;
      });

      // Déterminer le palier actif basé sur la quantité totale
      let activeTier = this.tiers[0]; // Par défaut : palier 1
      for (let tier of this.tiers) {
        if (totalQuantity >= tier.qty) {
          activeTier = tier;
        } else {
          break; // On s'arrête au premier palier non atteint
        }
      }

      // Calculer le prix total avec le prix du palier actif
      const unitPriceInCents = activeTier.price_cents || activeTier.qty * 100; // Fallback si pas de price_cents
      const totalPriceInCents = totalQuantity * unitPriceInCents;

      return {
        totalPriceInCents,
        unitPriceInCents,
        totalQuantity,
        activeTier
      };
    }

    updateQuantityMeter() {
      if (!this.quantityMeter) return;
      
      // Calculer la quantité totale
      let totalQuantity = 0;
      this.selectedVariants.forEach(data => {
        totalQuantity += data.quantity;
      });
      
      // Mettre à jour la barre de progression
      const progressLine = this.quantityMeter.querySelector('[data-progress-line-addon]');
      const items = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item');
      
      // Calculer le palier actuel
      let currentTier = 0;

      for (let i = 0; i < this.tiers.length; i++) {
        const tier = this.tiers[i];
        const circle = items[i]?.querySelector('.quantity-meter-addon-item__circle');
        const circleInner = items[i]?.querySelector('.quantity-meter-addon-item__circle-inner');
        const priceElement = items[i]?.querySelector('.quantity-meter-addon-item__price');

        if (totalQuantity >= tier.qty) {
          // Palier atteint
          circle?.classList.add('filled');
          circleInner?.classList.add('filled');
          priceElement?.classList.remove('subdued');
          currentTier = i;
        } else {
          // Palier non atteint
          circle?.classList.remove('filled');
          circleInner?.classList.remove('filled');
          priceElement?.classList.add('subdued');
        }
      }
      
      // Mettre à jour la barre de progression
      if (items.length > 0) {
        const firstCircle = items[0].querySelector('.quantity-meter-addon-item__circle');
        const lastCircle = items[items.length - 1].querySelector('.quantity-meter-addon-item__circle');
        const activeCircle = items[currentTier].querySelector('.quantity-meter-addon-item__circle');

        const CORRECTION = 10;
        const firstCenter = firstCircle.offsetLeft + firstCircle.offsetWidth / 2 + CORRECTION;
        const lastCenter = lastCircle.offsetLeft + lastCircle.offsetWidth / 2 + CORRECTION;
        const activeCenter = activeCircle.offsetLeft + activeCircle.offsetWidth / 2 + CORRECTION;

        // Barre de fond
        const bgLine = this.quantityMeter.querySelector('.quantity-meter-addon-line');
        if (bgLine) {
          bgLine.style.left = `${firstCenter}px`;
          bgLine.style.width = `${lastCenter - firstCenter}px`;
        }

        // Barre active
        if (progressLine && currentTier > 0) {
          progressLine.style.left = `${firstCenter}px`;
          progressLine.style.width = `${activeCenter - firstCenter}px`;
        } else if (progressLine) {
          progressLine.style.left = `${firstCenter}px`;
          progressLine.style.width = '0';
        }
      }
      
      // Mettre à jour les cadeaux
      this.updateGifts(totalQuantity);
    }
    
    updateGifts(totalQuantity) {
      // Envoyer un événement global pour les blocs gift-system indépendants
      const event = new CustomEvent('flavorCardsQuantityChanged', {
        detail: { 
          totalQuantity: totalQuantity,
          selectedVariants: Array.from(this.selectedVariants.entries())
        }
      });
      document.dispatchEvent(event);
    }

    updateProductForm() {
      if (!this.productForm) return;
      
      // Remove existing flavor cards inputs
      const existingInputs = this.productForm.querySelectorAll('input[data-flavor-cards-addon-input]');
      existingInputs.forEach(input => input.remove());
      
      // Always hide the main inputs when using flavor cards
      const mainVariantInput = this.productForm.querySelector('input[name="id"]');
      const mainQuantityInput = this.productForm.querySelector('input[name="quantity"]');
      
      if (mainVariantInput) {
        mainVariantInput.style.display = 'none';
        mainVariantInput.disabled = true;
      }
      if (mainQuantityInput) {
        mainQuantityInput.style.display = 'none';
        mainQuantityInput.disabled = true;
      }
      
      // If we have selected variants, add them as items array
      if (this.selectedVariants.size > 0) {
        let itemIndex = 0;
        this.selectedVariants.forEach((data, variantId) => {
          const idInput = document.createElement('input');
          idInput.type = 'hidden';
          idInput.name = `items[${itemIndex}][id]`;
          idInput.value = variantId;
          idInput.setAttribute('data-flavor-cards-addon-input', 'true');
          this.productForm.appendChild(idInput);
          
          const quantityInput = document.createElement('input');
          quantityInput.type = 'hidden';
          quantityInput.name = `items[${itemIndex}][quantity]`;
          quantityInput.value = data.quantity;
          quantityInput.setAttribute('data-flavor-cards-addon-input', 'true');
          this.productForm.appendChild(quantityInput);
          
          itemIndex++;
        });
      } else {
        // If no selections, add a dummy input to prevent default behavior
        const dummyInput = document.createElement('input');
        dummyInput.type = 'hidden';
        dummyInput.name = 'items[0][id]';
        dummyInput.value = '';
        dummyInput.setAttribute('data-flavor-cards-addon-input', 'true');
        this.productForm.appendChild(dummyInput);
        
        const dummyQuantityInput = document.createElement('input');
        dummyQuantityInput.type = 'hidden';
        dummyQuantityInput.name = 'items[0][quantity]';
        dummyQuantityInput.value = '0';
        dummyQuantityInput.setAttribute('data-flavor-cards-addon-input', 'true');
        this.productForm.appendChild(dummyQuantityInput);
      }
      
      // 🎯 MISE À JOUR DU PRIX DU BOUTON
      this.updateButtonPrice();
    }

    updateButtonPrice() {
      // Si aucune sélection, envoyer un événement pour réinitialiser le bouton
      if (this.selectedVariants.size === 0) {
        const event = new CustomEvent('flavorCardsPrice:change', {
          detail: {
            totalPrice: 0,
            selectedVariants: [],
            hasSelections: false
          }
        });
        document.dispatchEvent(event);
        return;
      }

      // Calculer le prix avec remise basé sur les paliers
      const priceInfo = this.calculateDiscountedPrice();

      // 🎯 Calculer le prix de base total (basePrice × quantité pour chaque variant)
      let totalBasePriceInCents = 0;
      this.selectedVariants.forEach((data) => {
        totalBasePriceInCents += data.basePrice * data.quantity;
      });

      // Mettre à jour le bouton via le ButtonPriceManager
      if (window.ButtonPriceManager && priceInfo.totalPriceInCents > 0) {
        // Créer un événement personnalisé avec le prix total avec remise
        const event = new CustomEvent('flavorCardsPrice:change', {
          detail: {
            totalPrice: priceInfo.totalPriceInCents,
            unitPrice: priceInfo.unitPriceInCents,
            totalQuantity: priceInfo.totalQuantity,
            activeTier: priceInfo.activeTier,
            basePriceTotal: totalBasePriceInCents, // 🎯 Prix de base total
            selectedVariants: Array.from(this.selectedVariants.entries()),
            hasSelections: this.selectedVariants.size > 0
          }
        });
        document.dispatchEvent(event);
      } else if (window.refreshButtonPrice) {
        // Fallback: méthode globale
        window.refreshButtonPrice();
      }
    }

    updateSummary() {
      const summaryContainer = this.container.querySelector('.flavor-cards-addon__summary');
      if (!summaryContainer) return;

      const summaryContent = summaryContainer.querySelector('.flavor-cards-addon__summary-content');
      if (!summaryContent) return;

      // Clear existing content
      summaryContent.innerHTML = '';

      if (this.selectedVariants.size === 0) {
        summaryContainer.classList.remove('has-selections');
        return;
      }

      summaryContainer.classList.add('has-selections');

      let totalPrice = 0;
      let totalQuantity = 0;

      // Add each selected variant
      this.selectedVariants.forEach((data) => {
        const itemTotal = (data.price * data.quantity) / 100;
        totalPrice += itemTotal;
        totalQuantity += data.quantity;

        const summaryItem = document.createElement('div');
        summaryItem.className = 'flavor-cards-addon__summary-item';
        summaryItem.innerHTML = `
          <span>${data.title} × ${data.quantity}</span>
          <span>$${itemTotal.toFixed(2)}</span>
        `;
        summaryContent.appendChild(summaryItem);
      });

      // Add total
      const totalItem = document.createElement('div');
      totalItem.className = 'flavor-cards-addon__summary-total';
      totalItem.innerHTML = `
        <span>Total (${totalQuantity} items)</span>
        <span>$${totalPrice.toFixed(2)}</span>
      `;
      summaryContent.appendChild(totalItem);
    }

    // Public methods for external integration
    getSelectedVariants() {
      return Array.from(this.selectedVariants.entries());
    }

    getTotalQuantity() {
      let total = 0;
      this.selectedVariants.forEach(data => {
        total += data.quantity;
      });
      return total;
    }

    getTotalPrice() {
      let total = 0;
      this.selectedVariants.forEach(data => {
        total += (data.price * data.quantity);
      });
      return total;
    }

    reset() {
      this.selectedVariants.clear();
      this.quantityInputs.forEach(input => {
        input.value = 0;
        this.updateQuantityButtons(input);
      });
      this.cards.forEach(card => {
        card.classList.remove('flavor-card-addon--selected');
      });
      this.updateQuantityMeter();
      this.updateProductForm();
      this.updateSummary();
    }
  }

  // Auto-initialize when DOM is ready
  function initializeFlavorCards() {
    const containers = document.querySelectorAll('[data-flavor-cards-addon]');
    containers.forEach(container => {
      if (!container.flavorCardsAddon) {
        container.flavorCardsAddon = new FlavorCardsAddon(container);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeFlavorCards);
  } else {
    initializeFlavorCards();
  }

  // Re-initialize on theme section loads (for theme editor)
  document.addEventListener('shopify:section:load', initializeFlavorCards);

  // Export for global access
  window.FlavorCardsAddon = FlavorCardsAddon;

})();
